import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, logout } from '../auth'; // Import auth helpers
import './Homepage.css';

// Import your logo
import luminaLogo from '../assets/luminalogo3.png';
import heroVideo from '../assets/hero-video.mp4';

import plaque1 from '../assets/new1.jpeg';
import plaque2 from '../assets/new4.jpeg';
import plaque3 from '../assets/new3.jpeg';

import acrylicMaterial from '../assets/ac.png.webp';
import aluminumMaterial from '../assets/al.webp';

const plaqueExamples = [
    { id: 1, image: plaque1, alt: 'Beispiel Gedenktafel 1' },
    { id: 2, image: plaque2, alt: 'Beispiel Gedenktafel 2' },
    { id: 3, image: plaque3, alt: 'Beispiel Gedenktafel 3' },
];

const Homepage = () => {
    const navigate = useNavigate();
    const [, setIsVisible] = useState({});
    const { scrollY } = useScroll();
    const opacity = useTransform(scrollY, [0, 300], [1, 0]);

    // Plaque slider state
    const [currentSlide, setCurrentSlide] = useState(0);

    // Authentication state
    const [loggedIn, setLoggedIn] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    // Check authentication status on mount
    useEffect(() => {
        const authenticated = isAuthenticated();
        setLoggedIn(authenticated);

        console.log('=== ADMIN STATUS DEBUG ===');

        // Check if user is admin - READ FROM JWT TOKEN
        if (authenticated) {
            const token = localStorage.getItem('lumina_jwt');
            console.log('1️⃣ Token exists:', !!token);

            console.log('=== FULL LOCALSTORAGE CHECK ===');

            // Prikaži SVE što je u localStorage
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                const value = localStorage.getItem(key);
                console.log(`🔑 ${key}:`, value);
            }

            if (token) {
                try {
                    // Dekoduj JWT token (payload je srednji deo)
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const payload = JSON.parse(window.atob(base64));

                    console.log('2️⃣ Token payload:', payload);
                    console.log('3️⃣ Roles:', payload.roles);

                    // Proveri da li ima ROLE_ADMIN
                    const hasAdminRole = payload.roles && payload.roles.includes('ROLE_ADMIN');
                    console.log('4️⃣ Has ROLE_ADMIN:', payload.roles?.includes('ROLE_ADMIN'));

                    setIsAdmin(hasAdminRole);
                } catch (e) {
                    console.error('❌ Error decoding token:', e);
                    setIsAdmin(false);
                }
            }
        } else {
            setIsAdmin(false);
        }
    }, []);

    // Scroll intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1, rootMargin: '-50px' }
        );

        document.querySelectorAll('[data-animate]').forEach((el) => {
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    // Auto-advance slider
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % plaqueExamples.length);
        }, 4000);
        return () => clearInterval(timer);
    }, []);

    // Handle logout
    const handleLogout = () => {
        logout();
        setLoggedIn(false);
        setIsAdmin(false);
        // Logout function already redirects to /login, but we can force it
    };

    // Handle order/template click - redirect to login if not authenticated
    const handleOrderClick = () => {
        if (loggedIn) {
            navigate('/order');
        } else {
            navigate('/login');
        }
    };

    // Scroll to section function
    const scrollToSection = (sectionId) => {
        const element = document.querySelector(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: 'easeOut' },
        },
    };

    return (
        <div className="homepage">
            {/* HERO SECTION */}
            <section className="hero">
                <div className="video-background">
                    <video autoPlay muted loop playsInline>
                        <source src={heroVideo} type="video/mp4" />
                    </video>
                    <div className="video-overlay" />
                </div>

                {/* SMART NAVIGATION BUTTONS - WITH ADMIN SUPPORT */}
                {loggedIn ? (
                    <>
                        {/* Admin or Profile button (left) */}
                        <motion.button
                            className="profile-btn"
                            onClick={() => navigate(isAdmin ? '/admin' : '/profile')}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {isAdmin ? 'Admin' : 'Profil'}
                        </motion.button>

                        {/* Logout button (right) */}
                        <motion.button
                            className="login-btn"
                            onClick={handleLogout}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Abmelden
                        </motion.button>
                    </>
                ) : (
                    <>
                        {/* Login button (only when NOT logged in) */}
                        <motion.button
                            className="login-btn"
                            onClick={() => navigate('/login')}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Anmelden
                        </motion.button>
                    </>
                )}

                <motion.div
                    className="hero-content"
                    style={{ opacity }}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                >
                    <motion.div
                        className="hero-logo"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5, delay: 0.3 }}
                    >
                        <img src={luminaLogo} alt="Lumina Logo" />
                    </motion.div>

                    <motion.div
                        className="hero-divider"
                        initial={{ width: 0 }}
                        animate={{ width: 80 }}
                        transition={{ duration: 1, delay: 0.9 }}
                    />

                    <motion.p
                        className="hero-tagline"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5, delay: 0.6 }}
                    >
                        Erinnerungen für die Ewigkeit — im Licht bewahrt
                    </motion.p>

                    <motion.button
                        className="hero-order-btn"
                        onClick={handleOrderClick}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5, delay: 1.2 }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Jetzt bestellen
                    </motion.button>
                </motion.div>
            </section>

            {/* 1. WHAT IS LUMINA SECTION */}
            <section className="about-lumina" id="about">
                <div className="container">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Was ist Lumina?
                    </motion.h2>
                    <motion.div
                        className="section-subtitle"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Die Bedeutung, die Licht trägt
                    </motion.div>

                    <motion.div
                        className="about-content"
                        id="about-section"
                        data-animate
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="about-text" variants={itemVariants}>
                            <h3>Lumina — das Licht, das uns leitet</h3>
                            <p>
                                Lumina stammt aus dem Lateinischen und bedeutet Licht, Strahlen und göttliche
                                Erleuchtung. Seit Jahrhunderten steht Licht für Ewigkeit, Hoffnung und Erinnerungen,
                                die nie verblassen.
                                <br />
                                <br />
                                Unsere Mission ist es, jedem geliebten Menschen ein digitales Denkmal zu widmen — ein
                                Ort, an dem Erinnerungen für immer weiterleben und für alle zugänglich sind, die sich
                                erinnern und Respekt erweisen möchten.
                            </p>
                        </motion.div>

                        <motion.div className="particles-container" variants={itemVariants}>
                            <div className="particles-glow" />
                            {[...Array(25)].map((_, index) => (
                                <div key={index} className="particle" />
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* 2. WHY LUMINA SECTION */}
            <section className="why-us" id="why-us">
                <div className="container">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Warum Lumina?
                    </motion.h2>
                    <motion.div
                        className="section-subtitle"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Was uns besonders macht
                    </motion.div>

                    <motion.div
                        className="why-us-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">✨</div>
                            <h4 className="why-us-title">100 % persönlich</h4>
                            <p className="why-us-text">
                                Jeder QR-Code wird individuell erstellt — so einzigartig wie der Mensch selbst.
                            </p>
                        </motion.div>

                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">🕊️</div>
                            <h4 className="why-us-title">Würdevoll erinnern</h4>
                            <p className="why-us-text">
                                Sorgfältig gestaltet, respektvoll umgesetzt und bewusst zurückhaltend.
                            </p>
                        </motion.div>

                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">🛡️</div>
                            <h4 className="why-us-title">Langlebig &amp; zuverlässig</h4>
                            <p className="why-us-text">
                                Für den langfristigen Einsatz im Außenbereich konzipiert — wetterfest und robust.
                            </p>
                        </motion.div>

                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">📱</div>
                            <h4 className="why-us-title">Einfach &amp; zugänglich</h4>
                            <p className="why-us-text">
                                Kein Download, keine Registrierung — ein Scan genügt.
                            </p>
                        </motion.div>

                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">🔒</div>
                            <h4 className="why-us-title">Sicher &amp; diskret</h4>
                            <p className="why-us-text">
                                Sensibler Umgang mit persönlichen Inhalten und strikte Vertraulichkeit.
                            </p>
                        </motion.div>

                        <motion.div className="why-us-item" variants={itemVariants}>
                            <div className="why-us-icon">💫</div>
                            <h4 className="why-us-title">Mehr als ein QR-Code</h4>
                            <p className="why-us-text">
                                Lumina bewahrt, was bleibt — Erinnerungen, die weiterleuchten.
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* 3. WHAT WE OFFER SECTION */}
            <section className="what-we-offer" id="pricing">
                <div className="container">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Unser Angebot
                    </motion.h2>
                    <motion.div
                        className="section-subtitle"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Komplette Lösungen für digitales Erinnern
                    </motion.div>

                    <motion.div
                        className="offer-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="offer-card" variants={itemVariants}>
                            <div className="offer-icon">🔲</div>
                            <h3 className="offer-title">QR-Gedenktafel</h3>
                            <p className="offer-description">
                                Eine hochwertige, wetterfeste Tafel mit QR-Code, der direkt zur Gedenkseite führt.
                                Gefertigt aus langlebigen Materialien für eine dauerhafte Nutzung im Außenbereich.
                                <br/>
                                <br/>
                                *Die Plakette ist in allen Paketen enthalten.
                            </p>
                        </motion.div>

                        <motion.div className="offer-card" variants={itemVariants}>
                            <div className="offer-icon">💻</div>
                            <h3 className="offer-title">Gedenkseite</h3>
                            <p className="offer-description">
                                Eine personalisierte digitale Seite mit bis zu 30 Fotos oder Videos und einer
                                vollständigen Biografie. 24/7 von jedem Gerät erreichbar und auf sicheren Servern
                                gespeichert.
                            </p>
                            <div className="offer-packages">
                                <div className="package-item">
                                    <strong>Basic (5 Jahre)</strong> — 179 € mit Verlängerungsoption
                                </div>
                                <div className="package-item">
                                    <strong>Premium (10 Jahre)</strong> — 319 € mit Verlängerungsoption
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* 4. HOW IT WORKS + OUR TEMPLATES SECTION */}
            <section className="how-it-works" id="how-it-works">
                <div className="container">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        So funktioniert’s
                    </motion.h2>
                    <motion.div
                        className="section-subtitle"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Fünf einfache Schritte zu einer ewigen Erinnerung
                    </motion.div>

                    <motion.div
                        className="steps-container"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="step" variants={itemVariants}>
                            <div className="step-number">1</div>
                            <h4 className="step-title">Anmelden</h4>
                            <p className="step-description">
                                Konto erstellen oder anmelden, um Ihr Gedenk-Dashboard zu öffnen
                            </p>
                        </motion.div>

                        <motion.div className="step" variants={itemVariants}>
                            <div className="step-number">2</div>
                            <h4 className="step-title">Design auswählen</h4>
                            <p className="step-description">
                                Wählen Sie Ihr bevorzugtes Design und die Laufzeit des Plans
                            </p>
                        </motion.div>

                        <motion.div className="step" variants={itemVariants}>
                            <div className="step-number">3</div>
                            <h4 className="step-title">Lieferdaten</h4>
                            <p className="step-description">
                                Geben Sie Ihre Lieferadresse für die QR-Gedenktafel ein
                            </p>
                        </motion.div>

                        <motion.div className="step" variants={itemVariants}>
                            <div className="step-number">4</div>
                            <h4 className="step-title">Seite erstellen</h4>
                            <p className="step-description">
                                Gestalten Sie die Gedenkseite mit Fotos, Texten und Erinnerungen
                            </p>
                        </motion.div>

                        <motion.div className="step" variants={itemVariants}>
                            <div className="step-number">5</div>
                            <h4 className="step-title">Tafel erhalten</h4>
                            <p className="step-description">
                                Erhalten Sie Ihre hochwertige QR-Gedenktafel — bereit zum Anbringen
                            </p>
                        </motion.div>
                    </motion.div>

                    {/* Our Templates Section - 6 TEMPLATES */}
                    <div className="templates-section">
                        <motion.h3
                            className="section-title"
                            style={{ fontSize: '36px' }}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            Unsere Designs
                        </motion.h3>
                        <motion.div
                            className="section-subtitle"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            Wählen Sie das Design, das Ihre Erinnerung am besten widerspiegelt
                        </motion.div>

                        <motion.div
                            className="templates-grid-six"
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {/* Template 1 - Gold */}
                            <motion.div
                                className="template-card classic"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Classic Gold</h4>
                                    <p>Elegantes Golddesign</p>
                                </div>
                            </motion.div>

                            {/* Template 2 - Silver */}
                            <motion.div
                                className="template-card silver"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Silver Elegance</h4>
                                    <p>Edle Silbertöne</p>
                                </div>
                            </motion.div>

                            {/* Template 3 - White */}
                            <motion.div
                                className="template-card white"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Pure White</h4>
                                    <p>Klar und zeitlos</p>
                                </div>
                            </motion.div>

                            {/* Template 4 - Beige */}
                            <motion.div
                                className="template-card beige"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Warm Beige</h4>
                                    <p>Sanft und beruhigend</p>
                                </div>
                            </motion.div>

                            {/* Template 5 - Black */}
                            <motion.div
                                className="template-card black"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Deep Black</h4>
                                    <p>Modern und würdevoll</p>
                                </div>
                            </motion.div>

                            {/* Template 6 - Ice Blue */}
                            <motion.div
                                className="template-card iceblue"
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                onClick={handleOrderClick}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="template-preview">
                                    <div className="template-header">Designvorschau</div>
                                    <div className="template-photo"></div>
                                    <div className="template-identity">
                                        <div className="template-name">Vollständiger Name</div>
                                        <div className="template-dates">01/01/1950 — 01/01/2020</div>
                                        <div className="template-nav">
                                            <div className="template-nav-item">Biografie</div>
                                            <div className="template-nav-item">Galerie</div>
                                            <div className="template-nav-item">Ort</div>
                                        </div>
                                    </div>
                                    <div className="template-bio">
                                        <div className="template-bio-label">Biografie-Bereich</div>
                                    </div>
                                </div>
                                <div className="template-info">
                                    <h4>Ice Blue</h4>
                                    <p>Ruhig und friedlich</p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 5. MATERIALS & SHAPES SECTION */}
            <section className="materials" id="materials">
                <div className="container">
                    <motion.h2
                        className="section-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        Materialien &amp; Formen
                    </motion.h2>
                    <motion.div
                        className="section-subtitle"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Premium-Qualität mit individuellen Optionen
                    </motion.div>

                    <motion.p
                        className="materials-intro"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        Unsere QR-Gedenktafeln bestehen aus hochwertigen Materialien, die Witterungseinflüssen,
                        UV-Strahlung und Temperaturschwankungen standhalten.
                    </motion.p>

                    {/* Materials Grid */}
                    <motion.div
                        className="materials-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="material-item" variants={itemVariants} whileHover={{ y: -5 }}>
                            <div className="material-preview">
                                <img src={acrylicMaterial} alt="Acrylmaterial" className="material-image" />
                            </div>
                            <div className="material-name">Acryl</div>
                        </motion.div>

                        <motion.div className="material-item" variants={itemVariants} whileHover={{ y: -5 }}>
                            <div className="material-preview">
                                <img
                                    src={aluminumMaterial}
                                    alt="Eloxiertes Aluminium"
                                    className="material-image"
                                />
                            </div>
                            <div className="material-name">Eloxiertes Aluminium</div>
                        </motion.div>
                    </motion.div>

                    {/* Plaque Examples Slider */}
                    <motion.div
                        className="plaque-slider-section"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <h4 className="slider-title">Unsere Gedenktafeln</h4>
                        <div className="plaque-slider">
                            <div className="slider-container">
                                {plaqueExamples.map((plaque, index) => (
                                    <div
                                        key={plaque.id}
                                        className={`slide ${index === currentSlide ? 'active' : ''}`}
                                    >
                                        <img src={plaque.image} alt={plaque.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="slider-dots">
                                {plaqueExamples.map((_, index) => (
                                    <button
                                        key={index}
                                        className={`dot ${index === currentSlide ? 'active' : ''}`}
                                        onClick={() => setCurrentSlide(index)}
                                        aria-label={`Zur Folie ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    <motion.p
                        className="custom-shape-note"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.7 }}
                    >
                        Standardformen: Quadrat &amp; Herz • Sonderformen auf Anfrage
                    </motion.p>
                </div>
            </section>

            {/* Footer */}
            <footer>
                <div className="footer-content">
                    <motion.img
                        src={luminaLogo}
                        alt="Lumina"
                        className="footer-logo"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    />

                    <motion.div
                        className="footer-contact"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <p>
                            Kontakt: <a href="mailto:contact@lumina.com">contact@lumina.com</a>
                        </p>
                    </motion.div>

                    <motion.div
                        className="footer-links"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('#about'); }}>Über</a>
                        <a href="#why-us" onClick={(e) => { e.preventDefault(); scrollToSection('#why-us'); }}>Warum Lumina</a>
                        <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('#how-it-works'); }}>So funktioniert's</a>
                        <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('#pricing'); }}>Preise</a>
                        <a href="#materials" onClick={(e) => { e.preventDefault(); scrollToSection('#materials'); }}>Materialien</a>
                        <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Datenschutz</a>
                        <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Allgemeine Geschäftsbedingungen</a>
                    </motion.div>

                    <motion.p
                        className="footer-copyright"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        © 2025 Lumina. Alle Rechte vorbehalten.
                    </motion.p>
                </div>
            </footer>
        </div>
    );
};

export default Homepage;
